import { useMutation, usePrefetchQuery, useQueryClient } from '@tanstack/react-query'
import { type FormEvent, useEffect, useRef, useState } from 'react'
import { possessive } from '../api/users'
import { currentConsent, loadAttribution } from '../checkout/attribution'
import {
  type BillingPeriod,
  MAX_CHILDREN,
  type Plan,
  type PlanId,
  type PurchasablePlan,
  planById,
} from '../checkout/catalogue'
import { readPlanLink } from '../checkout/link'
import {
  dueTodayCents,
  formatCents,
  orderSavingsCents,
  orderTotalCents,
  perChildRateCents,
  rateCents,
  siblingSavingsPercent,
} from '../checkout/pricing'
import { type CheckoutHandoff, createCheckout, sellingPlansQuery } from '../checkout/shopify'
import { type Child, checkoutForm, useCheckoutForm } from '../checkout/store'
import { activeCode, usePromo } from '../promo/store'
import { cartFor, itemFor, listFor, track, trackOnce } from '../runtime/analytics'
import { PlanPicker, PlanThumbnail } from './checkout/PlanPicker'
import { Button } from './ui/Button'
import { FormError } from './ui/FormError'
import { TextButton } from './ui/TextButton'
import { TextField } from './ui/TextField'

/**
 * The pre-checkout configurator. Collects a plan + billing period and each
 * child's name, then creates the cart on Shopify and navigates to its
 * checkout — no payment happens here, and Shopify asks for the parent's
 * email there.
 *
 * Two-step accordion: step 1 collapses to a summary with an Edit link, one
 * decision on screen at a time, everything revisable. Form state lives in the
 * module-scope store (`checkout/store.ts`), which drafts every change to
 * sessionStorage — a refresh or a detour around the marketing site loses
 * nothing, and several Checkout embeds on one page share one form.
 *
 * Focus is managed on every transition that unmounts its own trigger: step
 * changes land on the revealed step's heading, removing a kid row lands on a
 * surviving row, and the sibling autofocus is a one-shot. Without this,
 * keyboard and screen-reader users are silently dropped to <body>.
 */

export interface CheckoutProps {
  /**
   * Shop hostname override (`data-shop-domain` in Webflow). Typed unknown
   * because attribute coercion can deliver a non-string; the Shopify client
   * falls back to the production shop for anything unusable.
   */
  shopDomain?: unknown
  /** The contact page the Concierge card leads to. `data-contact-url`. */
  contactUrl?: string
}

const HANDOFF_ERROR_MESSAGE = 'We couldn’t open checkout. Please try again.'

export function Checkout({ shopDomain, contactUrl = '/contact' }: CheckoutProps) {
  const step = useCheckoutForm((state) => state.step)
  const billing = useCheckoutForm((state) => state.billing)
  const planId = useCheckoutForm((state) => state.planId)
  const children = useCheckoutForm((state) => state.children)
  const pendingFocusKey = useCheckoutForm((state) => state.pendingFocusKey)
  const promo = usePromo(activeCode)

  // Actions are stable references on the store; no need to subscribe to them.
  const form = checkoutForm.get()

  /**
   * Shown after a blocked submit attempt: the button is aria-disabled rather
   * than natively disabled, so keyboard and screen-reader users can reach it,
   * press it, and get told exactly what is missing — a natively disabled
   * button is unfocusable and silent, which turns invalid state into a dead
   * end. Visual styling is identical either way.
   */
  const [showValidation, setShowValidation] = useState(false)

  const kidInputs = useRef(new Map<number, HTMLInputElement>())
  const planHeading = useRef<HTMLHeadingElement>(null)
  const detailsHeading = useRef<HTMLHeadingElement>(null)
  const previousStep = useRef<'plan' | 'details' | null>(null)

  // Warm the selling-plan cache at mount, so Continue is normally one request.
  const queryClient = useQueryClient()
  usePrefetchQuery(sellingPlansQuery(shopDomain))

  const handoff = useMutation({
    mutationFn: async (request: CheckoutHandoff) => {
      const sellingPlans = await queryClient.fetchQuery(sellingPlansQuery(shopDomain))
      return createCheckout(request, sellingPlans)
    },
    onSuccess: (checkoutUrl) => window.location.assign(checkoutUrl),
    // Ours to debug; the visitor gets the generic message below.
    onError: (cause) => console.error('[codeverse] checkout handoff failed', cause),
  })

  // The plans on offer, once per page however many embeds show them and
  // whatever changes later, and the plan a pricing-page link chose, which the
  // store applied before the mount.
  useEffect(() => {
    trackOnce('list:checkout', {
      kind: 'view_items',
      list: 'checkout',
      items: listFor(promo, billing),
    })
    const link = readPlanLink(window.location.search)
    const linked = link && planById(link.planId, promo)
    if (linked) {
      trackOnce('link', { kind: 'select_item', list: 'pricing', item: itemFor(linked, billing) })
    }
  }, [promo, billing])

  // Step transitions unmount the control that triggered them (Continue, Edit),
  // dropping focus to <body>. Land it on the revealed step's heading instead —
  // but never on mount, or a draft-restored page would steal the page's focus.
  useEffect(() => {
    const previous = previousStep.current
    previousStep.current = step
    if (previous === null || previous === step) return
    const heading = step === 'details' ? detailsHeading : planHeading
    heading.current?.focus()
  }, [step])

  // Priced as the visitor's promo sells it, which is also what goes in the cart.
  const plan = planById(planId, promo)
  if (!plan) return null // unreachable: planId is validated at every entry point

  const { noun } = plan
  // The one thing that can block Continue, and where focus lands on a blocked attempt.
  const unnamed = children.find((child) => child.name.trim() === '')
  const canSubmit = unnamed === undefined

  function registerKidInput(key: number, element: HTMLInputElement | null) {
    if (element) kidInputs.current.set(key, element)
    else kidInputs.current.delete(key)
  }

  function removeChildAndRefocus(index: number, key: number) {
    // Focus a surviving neighbour before the removal unmounts the focused ✕.
    const neighbour = children[index - 1] ?? children[index + 1]
    if (neighbour) kidInputs.current.get(neighbour.key)?.focus()
    form.removeChild(key)
  }

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!plan) return

    if (unnamed) {
      setShowValidation(true)
      kidInputs.current.get(unnamed.key)?.focus()
      return
    }

    // Told now, while the cart is created, so the request outlives the
    // navigation that follows; a cart Shopify then refuses is the lesser error.
    track({ kind: 'add_to_cart', ...cartFor(plan, billing, children.length) })
    handoff.mutate({
      shopDomain,
      plan,
      billing,
      childNames: children.map((child) => child.name),
      attribution: loadAttribution(),
      consent: currentConsent(),
    })
  }

  function chooseBilling(next: BillingPeriod) {
    if (next !== billing) track({ kind: 'select_billing', billing: next })
    form.setBilling(next)
  }

  function choosePlan(id: PlanId) {
    const chosen = planById(id, promo)
    if (chosen) track({ kind: 'select_item', list: 'checkout', item: itemFor(chosen, billing) })
    form.choosePlan(id)
  }

  function addSibling() {
    track({ kind: 'add_sibling', kids: children.length + 1 })
    form.addChild()
  }

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-5">
      <section className="rounded-card bg-white shadow-xl">
        <header className="flex items-center justify-between border-b border-black/5 px-6 py-4">
          <h2
            ref={planHeading}
            tabIndex={-1}
            className="flex items-center gap-3 text-lg font-bold text-ink outline-none"
          >
            <StepBubble step={1} active />
            Pick your plan
          </h2>
          {step === 'details' && (
            <TextButton onClick={() => form.setStep('plan')} data-testid="edit-plan">
              Edit
            </TextButton>
          )}
        </header>
        <div className="p-5">
          {step === 'plan' ? (
            <PlanPicker
              billing={billing}
              planId={planId}
              promo={promo}
              contactUrl={contactUrl}
              onBillingChange={chooseBilling}
              onChoose={choosePlan}
            />
          ) : (
            <PlanSummary plan={plan} billing={billing} count={children.length} />
          )}
        </div>
      </section>

      <section
        className={
          step === 'details' ? 'rounded-card bg-white shadow-xl' : 'rounded-card bg-white/70'
        }
      >
        <header
          className={`flex items-center px-6 py-4 ${step === 'details' ? 'border-b border-black/5' : ''}`}
        >
          <h2
            ref={detailsHeading}
            tabIndex={-1}
            className={`flex items-center gap-3 text-lg font-bold outline-none ${
              step === 'details' ? 'text-ink' : 'text-ink-soft'
            }`}
          >
            <StepBubble step={2} active={step === 'details'} />
            Who is this for?
          </h2>
        </header>

        {step === 'details' && (
          /* A real <form> so Enter submits from any field. */
          <form onSubmit={onSubmit} noValidate className="p-5">
            {/* One attribute locks every control while the cart is being
                created, so what Shopify receives cannot drift from the screen. */}
            <fieldset disabled={handoff.isPending} data-testid="checkout-fields">
              <p className="text-sm text-ink-soft">
                We’ll use this to personalize each {noun} and set up access to our online coding
                platform.
              </p>

              {/* One stack, hairlines between rows: the step is the only box. */}
              <div className="mt-2 divide-y divide-black/5">
                {children.map((child, index) => (
                  <KidRow
                    key={child.key}
                    child={child}
                    index={index}
                    plan={plan}
                    billing={billing}
                    removable={children.length > 1}
                    shouldFocus={child.key === pendingFocusKey}
                    invalid={showValidation && child.name.trim() === ''}
                    onFocusConsumed={form.consumeFocus}
                    onRegisterInput={registerKidInput}
                    onRename={(name) => form.renameChild(child.key, name)}
                    onRemove={() => removeChildAndRefocus(index, child.key)}
                  />
                ))}

                {children.length < MAX_CHILDREN && (
                  <AddSiblingRow plan={plan} billing={billing} noun={noun} onAdd={addSibling} />
                )}
              </div>

              {showValidation && !canSubmit && (
                <FormError data-testid="checkout-validation">
                  Add each child’s first name to continue.
                </FormError>
              )}

              {handoff.isError && (
                <FormError data-testid="checkout-error">{HANDOFF_ERROR_MESSAGE}</FormError>
              )}

              <div className="mt-6">
                <Button
                  type="submit"
                  block
                  aria-disabled={!canSubmit}
                  data-testid="checkout-submit"
                >
                  {handoff.isPending ? 'Opening checkout…' : 'Continue'}
                </Button>
                <TotalLine plan={plan} billing={billing} count={children.length} noun={noun} />
              </div>
            </fieldset>
          </form>
        )}
      </section>
    </div>
  )
}

function StepBubble({ step, active }: { step: number; active: boolean }) {
  return (
    <span
      className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold ${
        active ? 'bg-brand text-white' : 'bg-brand-soft text-brand'
      }`}
    >
      {step}
    </span>
  )
}

function PlanSummary({
  plan,
  billing,
  count,
}: {
  plan: Plan
  billing: BillingPeriod
  count: number
}) {
  return (
    <div
      className="relative flex items-center gap-4 rounded-2xl bg-field/60 p-4"
      data-testid="plan-summary"
    >
      {count > 1 && (
        <span
          className="absolute -top-2 -left-2 rounded-lg bg-ink px-2 py-1 text-xs font-bold text-white"
          data-testid="kit-count-badge"
        >
          ×{count}
        </span>
      )}
      <PlanThumbnail plan={plan} />
      <div>
        <p className="text-xl font-bold text-ink" data-testid="summary-title">
          {plan.name}, {billing === 'annual' ? 'Annual' : 'Monthly'} Subscription
        </p>
        <p className="mt-1 text-sm text-ink-soft">{plan.description}</p>
      </div>
    </div>
  )
}

function KidRow({
  child,
  index,
  plan,
  billing,
  removable,
  shouldFocus,
  invalid,
  onFocusConsumed,
  onRegisterInput,
  onRename,
  onRemove,
}: {
  child: Child
  index: number
  plan: PurchasablePlan
  billing: BillingPeriod
  removable: boolean
  shouldFocus: boolean
  invalid: boolean
  onFocusConsumed: () => void
  onRegisterInput: (key: number, element: HTMLInputElement | null) => void
  onRename: (name: string) => void
  onRemove: () => void
}) {
  const inputRef = useRef<HTMLInputElement | null>(null)

  // Focus the name field of a just-added row, so adding a sibling costs zero
  // extra clicks. Consuming the store's one-shot key is what stops an Edit →
  // Continue round trip (which remounts every row) from re-stealing focus.
  useEffect(() => {
    if (shouldFocus) {
      inputRef.current?.focus()
      onFocusConsumed()
    }
  }, [shouldFocus, onFocusConsumed])

  const name = child.name.trim()
  const slot = `Account ${index + 1}`
  const eyebrow = name ? `${possessive(name)} Account` : slot

  return (
    <div className="py-4" data-testid={`kid-${index}`}>
      <div className="flex items-center justify-between gap-2">
        <p
          className={`text-xs font-bold ${name ? 'text-brand' : 'text-ink-soft'}`}
          data-testid={`kid-eyebrow-${index}`}
        >
          {/* The form becomes the product: name a child, watch it become their account. */}
          {eyebrow}
          {index > 0 && <span className="ml-2 font-medium text-ink-soft">· sibling discount</span>}
        </p>
        {removable && (
          <button
            type="button"
            onClick={onRemove}
            aria-label={`Remove ${name || slot}`}
            className="rounded-md p-1 text-ink-soft outline-none transition hover:bg-brand-soft hover:text-ink focus-visible:ring-2 focus-visible:ring-brand-ring"
            data-testid={`remove-child-${index}`}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" fill="none">
              <path
                d="m3 3 8 8m0-8-8 8"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
              />
            </svg>
          </button>
        )}
      </div>

      <div className="mt-3">
        <TextField
          ref={(element) => {
            inputRef.current = element
            onRegisterInput(child.key, element)
          }}
          label="Child’s first name"
          labelStyle="floating"
          /* Distinct accessible name per row — three fields all announcing
             "Child's first name" are indistinguishable in a form-controls list. */
          aria-label={`${eyebrow} — child’s first name`}
          autoComplete="off"
          autoCapitalize="words"
          autoCorrect="off"
          spellCheck={false}
          required
          invalid={invalid}
          value={child.name}
          onChange={(event) => onRename(event.target.value)}
          data-testid={`child-name-${index}`}
        />
      </div>

      {index > 0 && (
        <p className="mt-3 text-sm" data-testid={`kid-price-${index}`}>
          <s className="text-ink-soft">{formatCents(rateCents(plan, billing))}</s>{' '}
          <span className="font-semibold text-ink">
            {formatCents(perChildRateCents(plan, billing, index))}
          </span>{' '}
          <span className="text-ink-soft">/month</span>
        </p>
      )}
    </div>
  )
}

/** The last row of the stack: an action, styled as text so it adds no box. */
function AddSiblingRow({
  plan,
  billing,
  noun,
  onAdd,
}: {
  plan: PurchasablePlan
  billing: BillingPeriod
  noun: string
  onAdd: () => void
}) {
  return (
    <button
      type="button"
      onClick={onAdd}
      className="block w-full rounded-sm py-4 text-left text-sm font-medium text-brand outline-none transition hover:text-brand-hover focus-visible:ring-2 focus-visible:ring-brand-ring"
      data-testid="add-sibling"
    >
      + Add a sibling — save {siblingSavingsPercent(plan, billing)}% on their {noun}
    </button>
  )
}

function TotalLine({
  plan,
  billing,
  count,
  noun,
}: {
  plan: PurchasablePlan
  billing: BillingPeriod
  count: number
  noun: string
}) {
  const total = orderTotalCents(plan, billing, count)
  const dueToday = dueTodayCents(plan, billing, count)
  const savings = orderSavingsCents(plan, billing, count)
  const recurring = `${formatCents(total)}${billing === 'annual' ? ' a year' : '/mo'}`

  // An annual order is paid up front, so its total is what is due today. A
  // first charge discounted below the total is stated, then what follows it,
  // since Shopify shows no discount in the recurring price.
  const charges =
    dueToday !== total
      ? `${formatCents(dueToday)} due today, then ${recurring}`
      : billing === 'annual'
        ? `${formatCents(total)} due today`
        : recurring

  return (
    /* A polite live region: adding a kid or flipping billing announces the
       new money reality instead of repricing silently. */
    <p
      aria-live="polite"
      className="mt-3 text-center text-sm text-ink-soft"
      data-testid="order-total"
    >
      {count} {count === 1 ? noun : `${noun}s`} · {charges}
      {savings > 0 &&
        ` · you save ${formatCents(savings)} every ${billing === 'annual' ? 'year' : 'month'}`}
    </p>
  )
}
